// ─── Funnel Tracking Constants ────────────────────────────────────────────────

export const VIEW_KEYS = {
  // ── Sexual Health ──────────────────────────────────────────────────────────
  STH_LANDING: 'sth_landing',
  STH_CTA:     'sth_cta',

  // ── Subscription landing page (/subscribe) ─────────────────────────────────
  // One-week test for growth: CTA clicks, bounce rate, checkout conversion.
  // See scripts/subscription-report.js for how these become those numbers.
  SUB_LANDING:          'sub_landing',          // /subscribe viewed (note: traffic source)
  SUB_ENGAGED:          'sub_engaged',          // 10s on /subscribe with the tab visible
  SUB_CTA:              'sub_cta',              // left /subscribe for the sign-up page
  SUB_LEFT:             'sub_left',             // left /subscribe for any other page (note: where)
  SUB_SIGNUP:           'sub_signup',           // /subscribe/subscription viewed
  SUB_CHECKOUT:         'sub_checkout',         // /subscribe/checkout viewed
  SUB_CHECKOUT_SUCCESS: 'sub_checkout_success', // payment approved (note: package bought)

  // ── Homepage/services → checkout baseline (in-house flow) ──────────────────
  // Two funnels, distinguished at analysis time by where the session starts:
  //   1. HOME → CONDITION → ...   (condition picked off the homepage)
  //   2. SERVICES_LIST → CONDITION → ...   (browsed the services page)
  // Every step from CONDITION onward carries serviceId/serviceName so
  // drop-off can be broken down per condition.
  HOME:              'home',
  SERVICES_LIST:     'services_list',
  CONDITION:         'condition',
  DIAGNOSIS_START:   'diagnosis_start',
  MEDICAL_INFO:      'medical_info',
  TREATMENT_OPTIONS: 'treatment_options',
  PHARMACY_INFO:     'pharmacy_info',
  CHECKOUT:          'checkout',
  CHECKOUT_SUCCESS:  'checkout_success',
  ORDER_CONFIRMED:   'order_confirmed',

  // ── Step-level tracking inside the in-house flow ───────────────────────────
  // The keys above are one-per-page, so a visit that dies inside sign-up looks
  // the same as one that dies inside the questionnaire. These record each step
  // within a page, to show where the friction actually is.
  //
  // Most fire on *arrival* at a step: completing step N is arrival at N+1.
  // Where a step's outcome matters (a code rejected, a card declined) there's
  // an explicit event at the API boundary instead.

  // Sign-up, driven by currentStep in pages/diagnosis/index.js
  SIGNUP_WELCOME:         'signup_welcome',
  SIGNUP_ELIGIBILITY:     'signup_eligibility',     // DOB / weight / height / gender
  SIGNUP_ADDRESS:         'signup_address',
  SIGNUP_VERIFY:          'signup_verify',          // code screen reached
  SIGNUP_SIGNIN:          'signup_signin',          // existing user switched to log in

  // Verification, at the API boundaries in SignupFinalStep
  SIGNUP_CODE_SENT:       'signup_code_sent',       // note: initial | resend
  SIGNUP_CODE_SUBMITTED:  'signup_code_submitted',
  SIGNUP_CODE_REJECTED:   'signup_code_rejected',   // errorMessage carries why
  SIGNUP_ACCOUNT_CREATED: 'signup_account_created', // /user/signup verifies AND creates

  // Questionnaire. Groups are built per disease by groupQuestionsByType(), so
  // the section name travels in `note` rather than becoming its own key.
  MEDICAL_INTRO:       'medical_intro',
  MEDICAL_SECTION:     'medical_section',     // note: general|symptoms|causes|conditions|medications
  MEDICAL_ATTACHMENTS: 'medical_attachments',
  MEDICAL_COMPLETE:    'medical_complete',

  // Treatment options
  TREATMENT_SECTION:  'treatment_section',  // note: questions|priority|terms|std_terms
  TREATMENT_COMPLETE: 'treatment_complete',

  // Pharmacy and checkout
  PHARMACY_SUBMITTED:         'pharmacy_submitted',         // note: saved | manual
  CHECKOUT_PACKAGE:           'checkout_package',           // note: one_time | subscription
  CHECKOUT_PAYMENT_SUBMITTED: 'checkout_payment_submitted',
  CHECKOUT_PAYMENT_DECLINED:  'checkout_payment_declined',  // errorMessage carries why

  // Leaving the flow entirely (note: where they went, status: step left from)
  FLOW_EXIT: 'flow_exit',
};

// ── Paused ───────────────────────────────────────────────────────────────────
// track() drops these keys before sending anything; the calls stay on the
// pages, so deleting a key from this set is all it takes to resume it.
//
// Homepage/services baseline: ran 2026-09-05, paused 2026-09-14. Only its
//   three entry keys stay paused — the diagnosis keys below them were resumed
//   on 2026-09-25 because the step-level tracking needs them as its
//   denominator. Resuming HOME/SERVICES_LIST/CONDITION would restart that
//   baseline test, so they stay off.
// /subscribe test:            ran 2026-09-14, paused 2026-09-24.
// ── Whole-flow kill switch ───────────────────────────────────────────────────
// Paused 2026-10-09 at the owner's request: every DiagnosisFlowEvent is
// suppressed, not just the keys listed below. track() checks this first and
// returns before building or sending anything, so nothing reaches
// /api/tracking/funnel-event and no rows are written to the Parse class.
//
// Every call site across the flow stays exactly where it is. Set this back to
// true to resume the whole flow at once; PAUSED_VIEW_KEYS then applies again
// as normal for the individual tests that are off for their own reasons.
export const FUNNEL_TRACKING_ENABLED = false;

export const PAUSED_VIEW_KEYS = new Set([
  VIEW_KEYS.HOME,
  VIEW_KEYS.SERVICES_LIST,
  VIEW_KEYS.CONDITION,

  VIEW_KEYS.SUB_LANDING,
  VIEW_KEYS.SUB_ENGAGED,
  VIEW_KEYS.SUB_CTA,
  VIEW_KEYS.SUB_LEFT,
  VIEW_KEYS.SUB_SIGNUP,
  VIEW_KEYS.SUB_CHECKOUT,
  VIEW_KEYS.SUB_CHECKOUT_SUCCESS,
]);

export const EVENT_TYPES = {
  VIEWED:    'viewed',
  COMPLETED: 'completed',
};
